import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { minTouchTargetPx } from '../../tokens/spacing.js';
/**
 * The shared skin for <Button> and <ButtonLink>. One source so an anchor that
 * looks like a button can never drift from the real one.
 */
export function buttonStyle(variant, compact, ariaDisabled) {
    const base = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-2)',
        fontFamily: 'var(--font-ui)',
        fontSize: 'var(--font-size-2xs)',
        fontWeight: 500,
        borderRadius: 'var(--radius-input)',
        padding: compact ? `6px var(--space-3)` : `var(--space-2) var(--space-4)`,
        minHeight: compact ? `var(--control-compact-min-h, ${minTouchTargetPx}px)` : `${minTouchTargetPx}px`,
        cursor: 'pointer',
        textDecoration: 'none',
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
    };
    const skins = {
        primary: {
            background: 'var(--accent-cta-bg)',
            color: 'var(--accent-cta-text)',
            border: '1px solid var(--accent-cta-bg)',
        },
        // V98-6 review (Medium): the outline is the control's only edge, so it
        // uses the C23 interactive-edge token (>= 3:1 on every surface tier, WCAG
        // 1.4.11) — `--border-strong` measured 1.72:1.
        secondary: {
            background: 'var(--surface)',
            color: 'var(--text)',
            border: '1px solid var(--border-control)',
        },
        ghost: {
            background: 'transparent',
            color: 'var(--text)',
            border: '1px solid transparent',
        },
        // PR-P0-4 · destructive-with-typed-confirm affordance. Uses the
        // severity band-red tokens (Direction B) so light-mode and dark-
        // mode both render legibly. `--band-red-mark` is the strong red;
        // `--text-inverse` reads as near-white on the fill.
        // V101-8-5 review (WCAG 1.4.3): fill with `--band-red`, not `--band-red-mark` —
        // dark mode was 3.88:1 (#0F1219 on #DC2626); now 6.09:1 light / 6.77:1 dark.
        danger: {
            background: 'var(--band-red)',
            color: 'var(--text-inverse)',
            border: '1px solid var(--band-red)',
        },
    };
    // V101-5-4 review: an aria-disabled button (kept focusable so it can carry an
    // aria-describedby reason) must look inert. A flat inset skin + muted text
    // instead of opacity keeps the label at text-muted contrast (>= 4.5:1) on
    // every surface; the consumer still guards the click.
    const inert = ariaDisabled
        ? { cursor: 'not-allowed', background: 'var(--surface-inset)', color: 'var(--text-muted)', border: '1px solid var(--border)' }
        : {};
    return { ...base, ...skins[variant], ...inert };
}
export const Button = React.forwardRef(function Button({ variant = 'secondary', compact, className, style, children, type = 'button', ...rest }, ref) {
    const ariaDisabled = rest['aria-disabled'] === true || rest['aria-disabled'] === 'true';
    return (_jsx("button", { ref: ref, 
        // Default type="button" so a <Button> inside a <form> never
        // implicit-submits it. Consumers who want submit pass type="submit"
        // explicitly (e.g. the wizard's final Create button). Without this
        // default, a Remove/Delete button inside a form step advanced the
        // form on click AND turned Enter in any text input into a submit.
        type: type, "data-button": true, "data-variant": variant, "data-compact": compact || undefined, className: className, "data-inert": ariaDisabled || undefined, style: { ...buttonStyle(variant, compact, ariaDisabled), ...style }, ...rest, children: children }));
});
/**
 * <ButtonLink /> — a real anchor (`href`, history, open-in-new-tab) with the
 * <Button> skin. Use it for navigation that must look like a button (a CTA that
 * jumps to an in-page form, an external booking page). Same data attributes as
 * <Button>, so the C2 / C16 contracts and the pressed-state rule in
 * direction-b.css apply unchanged.
 */
export const ButtonLink = React.forwardRef(function ButtonLink({ variant = 'secondary', compact, className, style, children, ...rest }, ref) {
    return (_jsx("a", { ref: ref, "data-button": true, "data-variant": variant, "data-compact": compact || undefined, className: className, style: { ...buttonStyle(variant, compact, false), ...style }, ...rest, children: children }));
});
//# sourceMappingURL=Button.js.map